import React, { useState, useEffect } from 'react';
import styles from './CareerPage.module.css';
import SEO from '../components/SEO/SEO';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope, 
  faUsers, 
  faRocket, 
  faGraduationCap,
  faHeart,
  faCheckCircle,
  faFileAlt,
  faUserTie,
  faCode,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';

const CareerPage = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const benefits = [
    {
      icon: faRocket,
      title: 'Growth Opportunities',
      description: 'Accelerate your career with challenging projects and continuous learning'
    },
    {
      icon: faUsers,
      title: 'Collaborative Culture',
      description: 'Work with talented professionals in a supportive, inclusive environment'
    },
    {
      icon: faGraduationCap,
      title: 'Learning & Development',
      description: 'Access to training, conferences, and skill enhancement programs'
    },
    {
      icon: faHeart,
      title: 'Work-Life Balance',
      description: 'Flexible schedules and remote work options for better productivity'
    },
    {
      icon: faChartLine,
      title: 'Competitive Packages',
      description: 'Attractive compensation with performance-based incentives'
    },
    {
      icon: faCode,
      title: 'Cutting-Edge Tech',
      description: 'Work with the latest technologies and modern development practices'
    }
  ];


  return (
    <div className={styles.careerPage}>
      <SEO 
        title="Careers | Join SolveDot Labs - Software Development Jobs in Greater Noida"
        description="Join SolveDot Labs - a leading software development company. Explore career opportunities in AI, Web Development, IoT, and Manufacturing Software. Competitive packages, growth opportunities, and cutting-edge technology. Apply now!"
        keywords="SolveDot Labs Careers, Software Development Jobs, Tech Jobs Greater Noida, Software Engineer Jobs, Developer Jobs India, IT Jobs"
      />
      {/* Scroll Progress Indicator */}
      <div className={styles.scrollProgressBar}>
        <div 
          className={styles.scrollProgressFill}
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      {/* Enhanced Hero Header */}
      <section className={styles.heroHeader}>
        <div className={styles.heroBackground}>
          <div className={styles.heroOrb1}></div>
          <div className={styles.heroOrb2}></div>
        </div>
        <div className={styles.heroContent}>
          <span className={styles.heroBadge}>Join Our Team</span>
          <h1 className={styles.heroTitle}>
            Build Your Career.
            <br />
            <span className={styles.titleHighlight}>Shape the Future.</span>
          </h1>
          <p className={styles.heroDescription}>
            At SolveDot Labs, we're not just building software—we're building careers. Join a team of passionate innovators who are transforming businesses and creating impact. Your next opportunity starts here.
          </p>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className={styles.whyJoinSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Why Join Us</span>
          <h2 className={styles.sectionTitle}>What Makes Us Special</h2>
          <p className={styles.sectionDescription}>
            We believe in creating an environment where talent thrives, ideas flourish, and careers grow. Here's what sets us apart.
          </p>
        </div>
        <div className={styles.benefitsGrid}>
          {benefits.map((benefit, index) => (
            <div key={index} className={styles.benefitCard}>
              <div className={styles.benefitIconWrapper}>
                <div className={styles.benefitIconGlow}></div>
                <FontAwesomeIcon icon={benefit.icon} className={styles.benefitIcon} />
              </div>
              <h3 className={styles.benefitTitle}>{benefit.title}</h3>
              <p className={styles.benefitDescription}>{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Application Process Section */}
      <section className={styles.processSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Application Process</span>
          <h2 className={styles.sectionTitle}>How to Apply</h2>
          <p className={styles.sectionDescription}>
            Follow these simple steps to submit your application and join our team.
          </p>
        </div>
        <div className={styles.processSteps}>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>01</div>
            <div className={styles.stepIconWrapper}>
              <FontAwesomeIcon icon={faFileAlt} className={styles.stepIcon} />
            </div>
            <h3>Prepare Your Application</h3>
            <p>Update your resume/CV and prepare a cover letter highlighting your relevant experience and why you want to join SolveDot Labs.</p>
          </div>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>02</div>
            <div className={styles.stepIconWrapper}>
              <FontAwesomeIcon icon={faEnvelope} className={styles.stepIcon} />
            </div>
            <h3>Send Your Application</h3>
            <p>Email your application to <strong>careers@solvedotlabs.com</strong> with the position title in the subject line. Include your resume, cover letter, and any relevant portfolio links.</p>
          </div>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>03</div>
            <div className={styles.stepIconWrapper}>
              <FontAwesomeIcon icon={faUserTie} className={styles.stepIcon} />
            </div>
            <h3>Interview Process</h3>
            <p>Our team will review your application and contact you if there's a match. The process typically includes a phone screening, technical assessment, and team interviews.</p>
          </div>
          <div className={styles.processStep}>
            <div className={styles.stepNumber}>04</div>
            <div className={styles.stepIconWrapper}>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.stepIcon} />
            </div>
            <h3>Welcome Aboard</h3>
            <p>If selected, you'll receive an offer letter and onboarding details. We'll help you get started and integrate into our team smoothly.</p>
          </div>
        </div>
      </section>

      {/* Application Instructions Section */}
      <section className={styles.instructionsSection}>
        <div className={styles.instructionsContainer}>
          <div className={styles.instructionsHeader}>
            <div className={styles.instructionsIconWrapper}>
              <FontAwesomeIcon icon={faEnvelope} className={styles.instructionsIcon} />
            </div>
            <h2 className={styles.instructionsTitle}>Application Instructions</h2>
            <p className={styles.instructionsSubtitle}>
              Please follow these guidelines when submitting your application
            </p>
          </div>
          <div className={styles.instructionsContent}>
            <div className={styles.instructionCard}>
              <div className={styles.instructionNumber}>1</div>
              <div className={styles.instructionText}>
                <h3>Email Subject Line</h3>
                <p>Use the format: <strong>"Application for [Position Title]"</strong></p>
                <p className={styles.example}>Example: "Application for Senior Full-Stack Developer"</p>
              </div>
            </div>
            <div className={styles.instructionCard}>
              <div className={styles.instructionNumber}>2</div>
              <div className={styles.instructionText}>
                <h3>Required Documents</h3>
                <ul>
                  <li>Updated Resume/CV (PDF format preferred)</li>
                  <li>Cover Letter (optional but recommended)</li>
                  <li>Portfolio links (for design/development roles)</li>
                  <li>Any relevant certifications or achievements</li>
                </ul>
              </div>
            </div>
            <div className={styles.instructionCard}>
              <div className={styles.instructionNumber}>3</div>
              <div className={styles.instructionText}>
                <h3>Email Content</h3>
                <p>In your email, please include:</p>
                <ul>
                  <li>Brief introduction about yourself</li>
                  <li>Why you're interested in the position</li>
                  <li>Relevant experience and skills</li>
                  <li>Your availability and preferred work mode (remote/on-site)</li>
                </ul>
              </div>
            </div>
            <div className={styles.instructionCard}>
              <div className={styles.instructionNumber}>4</div>
              <div className={styles.instructionText}>
                <h3>Contact Information</h3>
                <p>Send your application to:</p>
                <div className={styles.emailContactBox}>
                  <FontAwesomeIcon icon={faEnvelope} className={styles.emailIcon} />
                  <a 
                    href="mailto:careers@solvedotlabs.com?subject=Career Inquiry"
                    className={styles.emailLink}
                  >
                    careers@solvedotlabs.com
                  </a>
                </div>
                <p className={styles.responseNote}>
                  We typically respond within 3-5 business days. If you don't hear back, feel free to follow up.
                </p>
              </div>
            </div>
          </div>
          <div className={styles.ctaBox}>
            <a 
              href="mailto:careers@solvedotlabs.com?subject=Career Inquiry"
              className={styles.primaryCta}
            >
              Send Your Application <span className={styles.arrow}>→</span>
            </a>
            <p className={styles.ctaNote}>
              Have questions? Feel free to reach out to us at the same email address.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareerPage;

